import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  QAlert,
  QAlertProps,
  QBadge,
  QBodyLayout,
  QBox,
  QButton,
  QButtonGroup,
  QCenter,
  QHeader,
  QHorizontalMetadata,
  QLastModifiedMetadata,
  QOwnerMetadata,
  QPageLoader,
  QStack,
  QTab,
  QTabList,
  QTabs,
  QTag,
  QTitle,
  QualioQThemeProvider,
  QVersionMetadata,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';

import {
  documentApi,
  TrainingAssessmentValidation,
} from '../../../../../api/document';
import { DocumentStatus } from '../../../../../api/model/document';

import { useTrainingAssessment } from '../../../TrainingAssessment/hooks/useTrainingAssessment';
import { Question } from '../../../TrainingAssessment/types/trainingAssessment';

import { getOwnerText } from '../../../../../util/UserUtils';
import { DocumentStatusIdToStatusConfigMap } from '../../../../../util/WorkspaceUtils';
import {
  isApprovedOrEffective,
  shouldCompleteTraining,
} from '../../RenderLogic';

import { trainingApi } from '../../../../../api/training';
import { useBodyBackground } from '../../../../../hooks/BodyBackground';
import { BannerPlaceholder } from '../../../BannerPlaceholder/index';
import { CredentialConfirmationModal } from '../../../CredentialConfirmationModal/index';
import { useNavigateAfterTraining } from '../hooks';
import { TrainingAssessmentQuestion } from './TrainingAssessmentQuestion';

type TrainingAssessmentForm = {
  [key: string]: number;
};

const getQuestionsForValidation = (
  questions: Question[],
  formStateAssessmentForValidation: TrainingAssessmentForm,
) => {
  const formStateKeys = Object.keys(formStateAssessmentForValidation);
  const questionsForValidation = [...questions];

  formStateKeys.forEach((key) => {
    const index = Number.parseInt(key.split('-')[1]) - 1;
    const anwsers = [...questionsForValidation[index].answers];
    // we set the is_correct property of that question's answers to the user's choice
    questionsForValidation[index].answers = anwsers.map((answer, idx) => {
      const indexOfSelectedAnswer = Number.parseInt(
        // either the QRadio or react hook form
        // transform the value to string
        // eslint-disable-next-line
        // @ts-ignore
        formStateAssessmentForValidation[key],
      );
      answer.is_correct = indexOfSelectedAnswer === idx;
      return answer;
    });
  });

  return questionsForValidation;
};

export const TrainingAssessmentView = () => {
  useBodyBackground('document-editor-overview-refresh', true);
  const { t } = useTranslation();
  const [hasValidationError, setHasValidationError] = useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState('');
  const [idsOfQuestionsWithWrongAnswers, setIdsOfQuestionsWithWrongAnswers] =
    useState<number[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [successfullyValidatedAssessment, setSuccessfullyValidatedAssessment] =
    useState<TrainingAssessmentValidation | null>(null);
  const { id: documentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const navigateAfterTraining = useNavigateAfterTraining();
  const { showToast } = useToastProvider();
  const { trainingPercentageComplete } = useFlags();

  const loadDocument = async () => {
    if (!documentId) {
      return;
    }

    const fetchedDocument = await documentApi.fetchByIdOrVersion(
      documentId,
      '',
      1,
      1,
    );

    return fetchedDocument;
  };

  const {
    isLoading: isLoadingDocument,
    data: qualioDocument,
    isError: isDocumentError,
  } = useQuery({
    queryKey: ['documentTraining', documentId],
    queryFn: loadDocument,
    refetchOnWindowFocus: false,
    onError: () => {
      showToast({
        'data-cy': 'training-assessment-load-error-toast',
        id: 'training-assessment-load-error',
        title: t('notifications.error'),
        description: t('errors.couldNotLoadAssessment'),
        status: 'error',
        replace: true,
      });
    },
  });

  const {
    data: trainingAssessment,
    isLoading: isLoadingTrainingAssessment,
    isError: isAssessmentError,
  } = useTrainingAssessment(documentId);

  const { control, handleSubmit, reset } = useForm<TrainingAssessmentForm>();

  useEffect(() => {
    if (trainingAssessment) {
      reset({});
    }
  }, [trainingAssessment, reset]);

  const alertProps: QAlertProps = useMemo(() => {
    return {
      title: hasValidationError
        ? t('modals.assessmentIncomplete')
        : t('messages.pleaseCompleteTrainingAssessment'),
      description: hasValidationError
        ? validationErrorMessage
        : t('messages.youMustAnswerQuestionsCorrectly'),
      status: hasValidationError ? 'error' : 'info',
    };
  }, [hasValidationError, validationErrorMessage, t]);

  const handleExitTraining = () => {
    const basePath = location.pathname.split('/training/')[0];
    void navigate(basePath);
  };

  const handleConfirmTraining = async (email: string, password: string) => {
    if (!qualioDocument || successfullyValidatedAssessment === null) {
      return;
    }
    try {
      await documentApi.signOffTraining(
        qualioDocument.id,
        email,
        password,
        successfullyValidatedAssessment,
      );
      showToast({
        'data-cy': 'training-sign-off-success-toast',
        id: 'training-sign-off-success',
        description: t('notifications.successfullyCompletedTraining'),
        status: 'success',
        title: t('notifications.trainingComplete'),
        replace: true,
      });
      navigateAfterTraining();
    } catch (error: unknown) {
      throw error;
    }
  };

  const validateAssessment: SubmitHandler<TrainingAssessmentForm> = async (
    assessmentForm,
  ) => {
    if (!documentId || !trainingAssessment) {
      return;
    }

    setIsValidating(true);
    setIdsOfQuestionsWithWrongAnswers([]);
    const docIdNumber = Number.parseInt(documentId);
    const validationPayload = {
      documentId: docIdNumber,
      id: trainingAssessment.id,
      questions: getQuestionsForValidation(
        trainingAssessment.questions,
        assessmentForm,
      ),
    };

    const result = await trainingApi.validateDocumentAssessment(
      currentUser.companyId,
      docIdNumber,
      validationPayload,
    );
    switch (result.slug) {
      case 'valid-answers':
        setIsModalOpen(true);
        setHasValidationError(false);
        setValidationErrorMessage('');
        setSuccessfullyValidatedAssessment(validationPayload);
        break;
      case 'valid-answers-with-wrong-answers':
        setIsModalOpen(true);
        setHasValidationError(false);
        setValidationErrorMessage('');
        setSuccessfullyValidatedAssessment({
          ...validationPayload,
          ...(trainingPercentageComplete
            ? {
                percentage_of_answers_correct:
                  result.percentage_of_answers_correct,
              }
            : {}),
        });
        break;
      case 'error':
        showToast({
          'data-cy': 'training-assessment-validation-error-toast',
          id: 'training-assessment-validation-error',
          title: t('notifications.error'),
          description: t('errors.couldNotValidateAssessment'),
          status: 'error',
          replace: true,
        });
        break;
      case 'wrong-answers-detailed':
        // this case could fall through the generic one, but the opinionated
        // linters don't allow that, if you put a ts-expect-error, you get
        // an eslint error, if you put a eslint exception you get a ts
        // compiler error, if you put both, the first one is not used
        // ...so I just repeat the code :D.
        setIdsOfQuestionsWithWrongAnswers(
          result.ids_of_questions_with_wrong_answers,
        );
        setHasValidationError(true);
        setValidationErrorMessage(result.message);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;
      case 'wrong-answers-generic':
        setHasValidationError(true);
        setValidationErrorMessage(result.message);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;
    }
    setIsValidating(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleTraining = async () => {
    await handleSubmit(validateAssessment)();
  };

  const handleSignOffError = () => {
    showToast({
      'data-cy': 'training-assessment-sign-off-error-toast',
      id: 'training-assessment-sign-off-error',
      title: t('notifications.error'),
      description: t('notifications.couldNotSignOffAssessment'),
      status: 'error',
      replace: true,
    });
  };

  if (!qualioDocument || isLoadingDocument || isLoadingTrainingAssessment) {
    return (
      <>
        <BannerPlaceholder />
        <QualioQThemeProvider>
          <QPageLoader />
        </QualioQThemeProvider>
      </>
    );
  }

  if (
    (!trainingAssessment && !isLoadingTrainingAssessment) ||
    !shouldCompleteTraining(qualioDocument, currentUser) ||
    !isApprovedOrEffective(qualioDocument.status_id)
  ) {
    handleExitTraining();
  }

  const documentStatusProps = qualioDocument?.status_id
    ? DocumentStatusIdToStatusConfigMap[
        qualioDocument.status_id as DocumentStatus
      ]
    : undefined;

  return (
    <>
      <BannerPlaceholder />
      <QualioQThemeProvider>
        <QBodyLayout.Default>
          <QHeader>
            <QTitle>
              {qualioDocument.title}{' '}
              <QBadge data-cy="document-code">{qualioDocument.code}</QBadge>
            </QTitle>

            <QHorizontalMetadata>
              <QTag
                data-cy="document-status"
                variantColor={documentStatusProps?.color}
              >
                {documentStatusProps?.label}
              </QTag>
              <QVersionMetadata
                data-cy="document-version"
                value={
                  qualioDocument.major_version +
                  '.' +
                  qualioDocument.minor_version
                }
              />
              <QOwnerMetadata
                data-cy="document-owner"
                value={getOwnerText(qualioDocument)}
              />
              <QLastModifiedMetadata
                data-cy="document-last-modified"
                value={new Date(qualioDocument.content_last_modified_at * 1000)}
              />
            </QHorizontalMetadata>
            <QButtonGroup>
              <QButton
                variant="outline"
                onClick={handleExitTraining}
                data-cy="doc-overview-exit-training"
              >
                {t('buttons.exitTrainingAssessment')}
              </QButton>
            </QButtonGroup>
          </QHeader>
          <QStack direction="row">
            <QBox width={'100%'}>
              <QTabs>
                <QTabList>
                  <QTab
                    data-cy="document-tab-training-assessment"
                    isDisabled={true}
                  >
                    {t('labels.trainingAssessment')}
                  </QTab>
                </QTabList>
              </QTabs>
            </QBox>
          </QStack>
          <QCenter mt={10}>
            <QStack spacing={10} width={'720px'}>
              <QStack spacing={4}>
                <QAlert {...alertProps} />
                {trainingAssessment?.questions.map((question, idx) => {
                  return (
                    <TrainingAssessmentQuestion
                      question={question}
                      index={idx + 1}
                      key={`question-${idx}`}
                      control={control}
                      hasWrongAnswer={idsOfQuestionsWithWrongAnswers.includes(
                        question.id!,
                      )}
                      data-cy={`question-${idx}`}
                    />
                  );
                })}
              </QStack>
              <QCenter mt={10}>
                <QButton
                  isDisabled={isAssessmentError || isDocumentError}
                  isLoading={isValidating}
                  variant="solid"
                  onClick={handleTraining}
                >
                  {t('buttons.submitCompleteTraining')}
                </QButton>
              </QCenter>
            </QStack>
          </QCenter>
        </QBodyLayout.Default>
        <CredentialConfirmationModal
          isOpen={isModalOpen}
          onCloseModal={handleCloseModal}
          handleStatusChange={handleConfirmTraining}
          modalHeading={t('modals.completeTraining')}
          modalText={t('modals.enterDigitalSignatureTraining')}
          buttonActionLabel={t('buttons.signOff')}
          withComment={false}
          handleError={handleSignOffError}
        />
      </QualioQThemeProvider>
    </>
  );
};
